/**
 * Featured Speakers — Obsidian Pulse living-network rail.
 * Design reminder: Syne-led keynote typography, electric-magenta invitation signals, leaf-green
 * availability cues, and restrained radiating-node motifs align this active-speaker rail with the event concepts.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { Link, useLocation } from "wouter";
import LordIcon from "@/components/LordIcon";
import { PROFILE_DETAILS, type SpeakerProfileDetail } from "@/lib/speakerProfileDetails";
import {
  ArrowRight,
  BrainCircuit,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Globe,
  Handshake,
  Linkedin,
  MapPin,
  Mic,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SectionBadge from "@/components/SectionBadge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSpeakerApplication } from "@/contexts/SpeakerApplicationContext";
import CtaIcon from "@/components/CtaIcon";
import { EVENTS, type CHIEvent } from "@/lib/eventsData";
import { personInitials } from "@/lib/personDisplay";
import {
  BUNDLED_SPEAKER_PORTRAITS,
  isMultipleSpeakerName,
  MULTIPLE_SPEAKERS_PORTRAIT,
} from "@/lib/speakerPortraits";
import { speakerTagForEvent } from "@/lib/speakerTopicTag";
import { trpc } from "@/lib/trpc";
import { eventFeaturedArtwork } from "@shared/eventPlaceholderArt";
import ResponsiveImage from "@/components/ResponsiveImage";
import {
  appStorageKeyFromUrl,
  isPubliclyServableStorageKey,
} from "@shared/storageContract";


type Speaker = SpeakerProfileDetail & {
  id: number;
  name: string;
  org: string;
  color: string;
  topic: string;
  tag: string;
  eventBlurb: string;
  eventDate: string;
  eventSortDate: string;
  eventTime: string;
  eventSlug: string;
  /** Flyer the speaker uploaded for this engagement, when there is one. */
  promotionalGraphicUrl?: string;
  /** Approved bundled portrait retried only when the published URL fails to load. */
  fallbackPhoto?: string;
};

const CHI_SPEAKER_TONES = ["#E91E8C", "#3DB658", "#DBA514"] as const;

/*
 * Publishing the CRM-backed roster and its production table are separate
 * operations. Keep the established public roster available until at least one
 * curated CRM row has been published, so a schema rollout cannot blank the page.
 */
const BUILT_IN_ROSTER_EVENT_SLUGS = [
  "stop-answering-same-questions-ai-hr-assistant",
  "scaling-hiring-infrastructure-growth",
  "hard-conversations-every-leader-needs",
  "the-human-advantage-building-community-age-ai",
  "evidence-based-decision-model",
  "the-secret-to-everyday-confidence",
] as const;

function toBuiltInSpeaker(event: CHIEvent): Speaker {
  return {
    ...PROFILE_DETAILS[event.slug],
    id: event.id,
    name: event.speaker,
    org: event.speakerOrg,
    color: CHI_SPEAKER_TONES[event.id % CHI_SPEAKER_TONES.length],
    topic: event.title,
    tag: event.category,
    eventBlurb: event.subtitle || event.description,
    eventDate: getShortDate(event.date),
    eventSortDate: event.date,
    eventTime: event.time,
    eventSlug: event.slug,
  };
}

const BUILT_IN_FEATURED_SPEAKERS = [
  ...BUILT_IN_ROSTER_EVENT_SLUGS
    .map(slug => EVENTS.find(event => event.slug === slug))
    .filter((event): event is CHIEvent => Boolean(event))
    .map(toBuiltInSpeaker),
  {
    ...PROFILE_DETAILS["advancing-hr-capabilities-with-ai"],
    id: 10_001,
    name: "Johnathan Tebeau",
    org: "Tebeau Group, LLC.",
    color: CHI_SPEAKER_TONES[0],
    topic: "Advancing HR Capabilities with AI",
    tag: "AI / Tech",
    eventBlurb: "Explore how AI can take on repetitive HR administration and create more room for strategic, human-centered leadership.",
    eventDate: "Dec 2",
    eventSortDate: "2026-12-02",
    eventTime: "1:00 PM – 3:00 PM",
    eventSlug: "advancing-hr-capabilities-with-ai",
  },
] satisfies Speaker[];


/** The fields this page reads from a published event. */
type CatalogEvent = {
  slug: string;
  title: string;
  date: string;
  time: string;
  category?: string | null;
  subtitle?: string | null;
  description?: string | null;
  speakerBio?: string | null;
};

/** "Sep 9" — the short form the cards and the rail both use. */
function getShortDate(date: string) {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" })
    .format(new Date(`${date}T00:00:00`));
}

/*
 * The curated roster now comes from the workspace.
 *
 * This held three hand-edited blocks: six event slugs, a table of profile details
 * keyed by event, and one card written out in full for a speaker whose record was
 * not ready. Changing who the page featured meant editing this file and deploying,
 * and a speaker appearing at two events had to be written twice.
 *
 * Featured cards are records now — Admin CRM › Speakers › Featured speakers — so
 * this file renders whatever is published and holds no roster of its own.
 */
function toCuratedSpeaker(
  row: {
    id: number;
    displayName: string;
    organization: string | null;
    headline: string | null;
    eventSlug: string | null;
    bio: string | null;
    photoUrl: string | null;
    linkedinUrl: string | null;
    websiteUrl: string | null;
    monogram: string | null;
    accentColor: string | null;
  },
  event?: CatalogEvent,
): Speaker {
  return {
    title: row.headline ?? "",
    icon: Mic,
    monogram: row.monogram ?? undefined,
    photo: row.photoUrl ?? undefined,
    bio: row.bio ?? "",
    linkedinUrl: row.linkedinUrl ?? undefined,
    websiteUrl: row.websiteUrl ?? undefined,
    id: row.id,
    name: row.displayName,
    org: row.organization ?? "",
    color: row.accentColor ?? CHI_SPEAKER_TONES[row.id % CHI_SPEAKER_TONES.length],
    /* The engagement supplies what belongs to the event, not to the card. */
    topic: event?.title ?? row.headline ?? "",
    tag: event?.category ?? "",
    eventBlurb: event?.subtitle || event?.description || row.bio || "",
    eventDate: event ? getShortDate(event.date) : "",
    eventSortDate: event?.date ?? "9999-12-31",
    eventTime: event?.time ?? "",
    eventSlug: row.eventSlug ?? "",
  };
}

/**
 * The next event, from the catalog it is given.
 *
 * Took the bundled `EVENTS` array before, so the page could name an event the
 * database no longer had — or miss one it did. The caller passes the published
 * catalog instead.
 */
function getNearestUpcomingEvent(catalog: readonly CatalogEvent[]): CatalogEvent | undefined {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const nearest = [...catalog]
    .filter((event) => new Date(`${event.date}T00:00:00`) >= startOfToday)
    .sort((a, b) => a.date.localeCompare(b.date))[0];

  /*
   * Undefined for an empty catalog, and said so in the type. The bundled array it
   * replaced could never be empty, so both callers dereferenced the result — from
   * the database that is a blank page on a site with no events yet.
   */
  return nearest ?? [...catalog].sort((a, b) => a.date.localeCompare(b.date))[0];
}

function publishedSpeakerMonogram(name: string) {
  return personInitials(name, "SP");
}

function isUnservableStoragePath(url: string | null) {
  const key = appStorageKeyFromUrl(url);
  return key !== null && !isPubliclyServableStorageKey(key);
}

function toPublishedSpeaker(speaker: {
  id: number;
  name: string;
  organization: string;
  professionalTitle: string;
  bio: string;
  linkedinUrl: string | null;
  portraitUrl: string | null;
  eventSlug: string;
  eventDate: string;
  eventTime: string;
  topic: string;
  eventBlurb: string;
  promotionalGraphicUrl: string | null;
}): Speaker {
  const bundledPortrait = BUNDLED_SPEAKER_PORTRAITS[speaker.name];
  const publishedPortrait =
    speaker.portraitUrl && !isUnservableStoragePath(speaker.portraitUrl)
      ? speaker.portraitUrl
      : null;
  return {
    id: speaker.id,
    name: speaker.name,
    org: speaker.organization,
    title: speaker.professionalTitle,
    /*
     * A valid published Replit App Storage URL is authoritative. Bundled media
     * remains only as the fallback for a missing portrait or for a private
     * source/candidate key that the public proxy intentionally refuses.
     */
    photo: isMultipleSpeakerName(speaker.name)
      ? MULTIPLE_SPEAKERS_PORTRAIT
      : publishedPortrait ?? bundledPortrait ?? "",
    fallbackPhoto:
      publishedPortrait && bundledPortrait && publishedPortrait !== bundledPortrait
        ? bundledPortrait
        : undefined,
    bio: speaker.bio,
    linkedinUrl: speaker.linkedinUrl ?? undefined,
    icon: Mic,
    monogram: publishedSpeakerMonogram(speaker.name),
    color: CHI_SPEAKER_TONES[speaker.id % CHI_SPEAKER_TONES.length],
    topic: speaker.topic,
    tag: speakerTagForEvent(speaker.eventSlug),
    eventBlurb: speaker.eventBlurb,
    eventDate: getShortDate(speaker.eventDate),
    eventSortDate: speaker.eventDate,
    eventTime: speaker.eventTime,
    eventSlug: speaker.eventSlug,
    promotionalGraphicUrl: speaker.promotionalGraphicUrl ?? undefined,
  };
}

function wrapIndex(index: number, length: number) {
  return length > 0 ? ((index % length) + length) % length : 0;
}

function getEarliestUpcomingSpeakerIndex(speakers: Speaker[]) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, "0"), String(today.getDate()).padStart(2, "0")].join("-");
  const index = speakers.findIndex(speaker => speaker.eventSortDate >= todayKey);
  return index >= 0 ? index : 0;
}

function SpeakerCarouselCard({
  speaker,
  offset,
  onSelect,
  onOpenDetails,
}: {
  speaker: Speaker;
  offset: number;
  onSelect: () => void;
  onOpenDetails: () => void;
}) {
  const SpeakerIcon = speaker.icon;
  const isActive = offset === 0;
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [portraitFailed, setPortraitFailed] = useState(false);
  const [useFallbackPortrait, setUseFallbackPortrait] = useState(false);
  const portraitSrc = useFallbackPortrait ? speaker.fallbackPhoto : speaker.photo;

  useEffect(() => {
    setPortraitFailed(false);
    setUseFallbackPortrait(false);
  }, [speaker.photo, speaker.fallbackPhoto]);

  const revealMobileDetails = () => {
    if (typeof window === "undefined" || !window.matchMedia("(max-width: 560px)").matches || isDetailsOpen) {
      return false;
    }

    setIsDetailsOpen(true);
    return true;
  };

  const activateCard = () => {
    if (!isActive) {
      onSelect();
      return;
    }

    if (revealMobileDetails()) return;
    onOpenDetails();
  };

  return (
    <article
      className={`speaker-carousel-card${isActive ? " speaker-carousel-card--active" : ""}${isDetailsOpen ? " speaker-carousel-card--details-open" : ""}`}
      data-offset={offset}
      data-topic-layout={speaker.eventSlug === "the-human-advantage-building-community-age-ai" ? "flush" : undefined}
      data-testid={`speaker-card-${speaker.eventSlug}`}
      data-speaker-key={`${speaker.id}-${speaker.eventSlug}`}
      style={{ "--speaker-color": speaker.color } as React.CSSProperties}
      role="link"
      tabIndex={0}
      aria-current={isActive ? "true" : undefined}
      aria-expanded={isDetailsOpen || undefined}
      aria-label={isActive
        ? `${speaker.name}: ${speaker.topic}. Open speaker details.`
        : `${speaker.name}: ${speaker.topic}. Move this speaker to the center of the carousel.`}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("a")) return;
        activateCard();
      }}
      onKeyDown={(event) => {
        if ((event.target as HTMLElement).closest("a")) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activateCard();
        }
      }}
    >
      {portraitSrc && !portraitFailed ? (
        /*
          Loading priority tracks the card's position in the stack.

          The centre card is the page's LCP candidate, so it loads eagerly at high
          priority. The four flanking cards are decoration until the reader moves
          the rail: on desktop they are on screen but scaled down and dimmed, and
          below 900px the stage is five 58–74vw columns, so the outer pair sits
          off-screen entirely. Marking them lazy/low lets the browser fetch the
          centre portrait first and defer the ones nobody is reading — it does not
          withhold an image that is genuinely visible, since lazy still loads
          anything in the viewport.

          No width/height: the image is absolutely positioned at inset:0 filling a
          card whose height CSS fixes, so there is no space to reserve.
        */
        <ResponsiveImage
          className="speaker-carousel-card__image"
          src={portraitSrc}
          alt={`${speaker.name} studio portrait`}
          loading={isActive ? "eager" : "lazy"}
          fetchPriority={isActive ? "high" : "low"}
          decoding="async"
          onError={() => {
            if (!useFallbackPortrait && speaker.fallbackPhoto) {
              setUseFallbackPortrait(true);
              return;
            }
            setPortraitFailed(true);
          }}
          maxWidth={640}
          sizes="(max-width: 900px) 74vw, 320px"
        />
      ) : (
        <div className="speaker-carousel-card__portrait-placeholder" aria-hidden="true">
          <span>{speaker.monogram}</span>
        </div>
      )}
      <div className="speaker-carousel-card__image-shade" />
      <div className="speaker-carousel-card__color-wash" />

      <p className="speaker-carousel-card__tag">{speaker.tag}</p>

      {speaker.linkedinUrl && (
        <a
          className="speaker-carousel-card__linkedin"
          href={speaker.linkedinUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${speaker.name}'s LinkedIn profile in a new tab`}
          title={`${speaker.name} on LinkedIn`}
          onClick={(event) => event.stopPropagation()}
        >
          <Linkedin size={16} aria-hidden="true" />
          <span className="sr-only">Open LinkedIn profile</span>
        </a>
      )}

      <div className="speaker-carousel-card__content">
        <div className="speaker-carousel-card__identity">
          <p className="speaker-carousel-card__name">{speaker.name}</p>
          <p className="speaker-carousel-card__role">{speaker.title}</p>
        </div>

        <h2 className="speaker-carousel-card__topic">{speaker.topic}</h2>
      </div>

      <div className="speaker-carousel-card__date">
        <CalendarDays size={14} aria-hidden="true" />
        <span>
          <strong>{speaker.eventDate}</strong>
          <small>{speaker.eventTime}</small>
        </span>
        <ArrowRight size={15} className="speaker-carousel-card__date-arrow" aria-hidden="true" />
      </div>
    </article>
  );
}

function SpeakerDetailDrawer({
  speaker,
  speakers,
  eventGraphicBySlug,
  open,
  onOpenChange,
  onSpeakerChange,
}: {
  speaker: Speaker | null;
  speakers: Speaker[];
  eventGraphicBySlug: ReadonlyMap<string, string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSpeakerChange: (speaker: Speaker) => void;
}) {
  const [, setLocation] = useLocation();
  const drawerBodyRef = useRef<HTMLDivElement>(null);
  const biographyRef = useRef<HTMLParagraphElement>(null);
  const biographyPauseUntilRef = useRef(0);
  const [portraitFailed, setPortraitFailed] = useState(false);
  const [biographyHasMore, setBiographyHasMore] = useState(false);

  useEffect(() => {
    setPortraitFailed(false);
  }, [speaker?.photo]);

  useEffect(() => {
    const biography = biographyRef.current;
    if (!open || !speaker || !biography) return;

    biography.scrollTop = 0;
    biographyPauseUntilRef.current = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let frame = 0;
    let previousTime = 0;
    let direction = 1;
    let holdUntil = performance.now() + 1_200;

    const updateOverflowCue = () => {
      setBiographyHasMore(
        biography.scrollHeight - biography.scrollTop - biography.clientHeight > 2,
      );
    };
    updateOverflowCue();

    if (reducedMotion || biography.scrollHeight <= biography.clientHeight) {
      return;
    }

    const tick = (time: number) => {
      const elapsed = previousTime ? Math.min(time - previousTime, 50) : 0;
      previousTime = time;
      if (
        time >= holdUntil
        && time >= biographyPauseUntilRef.current
        && document.visibilityState === "visible"
      ) {
        const maximum = biography.scrollHeight - biography.clientHeight;
        const next = biography.scrollTop + direction * elapsed * 0.012;
        if (next >= maximum) {
          biography.scrollTop = maximum;
          direction = -1;
          holdUntil = time + 1_500;
        } else if (next <= 0) {
          biography.scrollTop = 0;
          direction = 1;
          holdUntil = time + 1_500;
        } else {
          biography.scrollTop = next;
        }
        updateOverflowCue();
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [open, speaker]);

  if (!speaker) return null;

  const SpeakerIcon = speaker.icon;
  const event = EVENTS.find(candidate => candidate.slug === speaker.eventSlug);
  /*
   * Match the Calendar event cards, through the same resolver they use. Speaker
   * portraits and speaker-uploaded flyers belong to their own surfaces, and the
   * resolver never substitutes either.
   */
  const eventArtwork = eventFeaturedArtwork({
    slug: speaker.eventSlug,
    title: speaker.topic,
    eventImage: event?.eventImage,
    heroImage: event?.heroImage,
    generatedImage: eventGraphicBySlug.get(speaker.eventSlug),
  }, { width: 1200, height: 675 });
  const selectedSpeakerIndex = speakers.findIndex(candidate => candidate.id === speaker.id && candidate.eventSlug === speaker.eventSlug);
  const hasMultipleSpeakers = selectedSpeakerIndex >= 0 && speakers.length > 1;
  /*
    Only links that actually exist reach the follow row — a speaker with no
    published links renders no row at all rather than an empty strip of
    placeholder icons.
  */
  const followLinks = [
    speaker.linkedinUrl
      ? { key: "linkedin", href: speaker.linkedinUrl, icon: Linkedin, label: "LinkedIn profile" }
      : null,
    speaker.websiteUrl
      ? { key: "website", href: speaker.websiteUrl, icon: Globe, label: "website" }
      : null,
  ].filter((link): link is NonNullable<typeof link> => Boolean(link));

  const moveToSpeaker = (amount: number) => {
    if (!hasMultipleSpeakers) return;
    const nextSpeaker = speakers[wrapIndex(selectedSpeakerIndex + amount, speakers.length)];
    if (!nextSpeaker) return;
    onSpeakerChange(nextSpeaker);
    window.requestAnimationFrame(() => {
      drawerBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };
  const pauseBiographyAutoScroll = () => {
    biographyPauseUntilRef.current = performance.now() + 4_000;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="speaker-detail-drawer"
      >
        <SheetHeader className="speaker-detail-drawer__header">
          <div className="speaker-detail-drawer__identity">
            <div
              className="speaker-detail-drawer__portrait"
              style={{ "--speaker-color": speaker.color } as React.CSSProperties}
            >
              {speaker.photo && !portraitFailed ? (
                <ResponsiveImage
                  src={speaker.photo}
                  alt={`${speaker.name} portrait`}
                  loading="eager"
                  decoding="async"
                  onError={() => setPortraitFailed(true)}
                  maxWidth={320}
                  sizes="112px"
                />
              ) : (
                <div className="speaker-detail-drawer__portrait-fallback" aria-hidden="true">
                  <span>{speaker.monogram}</span>
                </div>
              )}
            </div>
            <div className="speaker-detail-drawer__identity-text">
              <SheetTitle className="speaker-detail-drawer__title">{speaker.name}</SheetTitle>
              <SheetDescription className="speaker-detail-drawer__subtitle">
                {[speaker.title, speaker.org].filter(Boolean).join(" · ")}
              </SheetDescription>
              {followLinks.length > 0 && (
                <div className="speaker-detail-drawer__follow-row" aria-label={`Follow ${speaker.name}`}>
                  {followLinks.map(({ key, href, icon: LinkIcon, label }) => (
                    <a key={key} className="speaker-detail-drawer__follow-link" href={href} target="_blank" rel="noopener noreferrer" aria-label={`Open ${speaker.name}'s ${label} in a new tab`}>
                      <LinkIcon size={17} aria-hidden="true" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        <div ref={drawerBodyRef} className="speaker-detail-drawer__body speaker-detail-drawer__timeline">
          <section className="speaker-detail-drawer__section speaker-detail-drawer__section--about" aria-label={`${speaker.name} biography`}>
            <span className="speaker-detail-drawer__timeline-beam speaker-detail-drawer__timeline-beam--about" aria-hidden="true" />
            <span className="speaker-detail-drawer__timeline-node speaker-detail-drawer__timeline-node--about" aria-hidden="true"><SpeakerIcon size={26} strokeWidth={1.65} /></span>
            <p className="speaker-detail-drawer__section-label">About {speaker.name.split(" ")[0]}</p>
            <p
              ref={biographyRef}
              className={`speaker-detail-drawer__biography${biographyHasMore ? " speaker-detail-drawer__biography--overflowing" : ""}`}
              tabIndex={0}
              aria-label={`${speaker.name} biography. Scroll for more.`}
              onScroll={() => {
                const biography = biographyRef.current;
                if (!biography) return;
                setBiographyHasMore(
                  biography.scrollHeight - biography.scrollTop - biography.clientHeight > 2,
                );
              }}
              onPointerDown={pauseBiographyAutoScroll}
              onWheel={pauseBiographyAutoScroll}
              onTouchStart={pauseBiographyAutoScroll}
              onFocus={pauseBiographyAutoScroll}
            >
              {speaker.bio}
            </p>
          </section>

          <section className="speaker-detail-drawer__event" aria-label="Upcoming talk">
            <span className="speaker-detail-drawer__timeline-node speaker-detail-drawer__timeline-node--event" aria-hidden="true"><CalendarDays size={25} strokeWidth={1.65} /></span>
            <p className="speaker-detail-drawer__section-label">Upcoming talk</p>
            <button
              type="button"
              className="speaker-detail-drawer__event-card"
              onClick={() => {
                onOpenChange(false);
                setLocation(`/events/${speaker.eventSlug}`);
              }}
            >
              <span className="speaker-detail-drawer__event-art" aria-hidden="true">
                <ResponsiveImage src={eventArtwork.url} alt="" loading="lazy" maxWidth={320} sizes="140px" />
              </span>
              <span className="speaker-detail-drawer__event-card-copy">
                <strong>{speaker.topic}</strong>
                <span className="speaker-detail-drawer__event-meta">
                  <small><CalendarDays size={14} aria-hidden="true" />{speaker.eventDate}</small>
                  <small><Clock3 size={14} aria-hidden="true" />{speaker.eventTime}</small>
                </span>
                <small className="speaker-detail-drawer__event-location"><MapPin size={14} aria-hidden="true" />{event?.location || "Online event"}</small>
              </span>
              <ChevronRight size={24} aria-hidden="true" />
            </button>
          </section>

        </div>

        {hasMultipleSpeakers && (
          <footer className="speaker-detail-drawer__pager" aria-label="Browse speaker profiles">
            <button type="button" onClick={() => moveToSpeaker(-1)} aria-label="Show previous speaker profile">
              <ChevronLeft size={20} aria-hidden="true" />
              <span>Prev</span>
            </button>
            <p aria-live="polite"><span>{selectedSpeakerIndex + 1}</span> / {speakers.length}</p>
            <button type="button" onClick={() => moveToSpeaker(1)} aria-label="Show next speaker profile">
              <span>Next</span>
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          </footer>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** Copies of the roster laid end to end, so the wheel never reaches an edge. */
const WHEEL_COPIES = 3;

function ContinuousSpeakerCarousel() {
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const [pendingSpeakerKey, setPendingSpeakerKey] = useState<string | null>(null);
  const hasUserNavigatedRef = useRef(false);
  const railRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  /**
   * The wheel's position, counted in tiles and never wrapped.
   *
   * Kept outside React because it has to stay continuous: stepping from the last
   * speaker to the first is one tile forward, and a wrapped index would describe
   * it as `length - 1` tiles backward and animate the whole rail the wrong way.
   */
  const positionRef = useRef(0);
  /** Which tile is centred, so the card can style and label itself. */
  const [activePosition, setActivePosition] = useState(0);
  const [, setLocation] = useLocation();
  const approvedSpeakersQuery = trpc.speakers.publicList.useQuery(undefined, { staleTime: 30_000 });
  const curatedQuery = trpc.speakers.featured.publicList.useQuery(undefined, { staleTime: 30_000 });
  const catalogQuery = trpc.events.publicCatalog.useQuery(undefined, { staleTime: 60_000 });
  const catalog = useMemo<CatalogEvent[]>(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const eventGraphicsQuery = trpc.events.publicGraphics.useQuery(undefined, {
    retry: false,
    staleTime: 30_000,
  });
  const eventGraphicBySlug = useMemo(
    () => new Map((eventGraphicsQuery.data ?? []).map(graphic => [graphic.eventSlug, graphic.url])),
    [eventGraphicsQuery.data],
  );
  const speakers = useMemo(() => {
    const publishedSpeakers = (approvedSpeakersQuery.data ?? []).map(toPublishedSpeaker);
    const publishedEventSlugs = new Set(publishedSpeakers.map(speaker => speaker.eventSlug));
    const eventBySlug = new Map(catalog.map(event => [event.slug, event]));
    /*
     * A curated card yields to an approved member speaker for the same event: the
     * published portrait and record are the better source once they exist, and the
     * card was the stopgap.
     */
    const managedCurated = (curatedQuery.data ?? [])
      .map(row => toCuratedSpeaker(row, row.eventSlug ? eventBySlug.get(row.eventSlug) : undefined))
      .filter(speaker => !speaker.eventSlug || !publishedEventSlugs.has(speaker.eventSlug));
    const managedNames = new Set(managedCurated.map(speaker => speaker.name.trim().toLowerCase()));
    const managedEventSlugs = new Set(managedCurated.map(speaker => speaker.eventSlug).filter(Boolean));
    const curated = [
      ...managedCurated,
      ...BUILT_IN_FEATURED_SPEAKERS.filter(speaker => (
        !managedNames.has(speaker.name.trim().toLowerCase())
        && (!speaker.eventSlug || (!managedEventSlugs.has(speaker.eventSlug) && !publishedEventSlugs.has(speaker.eventSlug)))
      )),
    ];
    return [...curated, ...publishedSpeakers].sort((a, b) => (
      a.eventSortDate.localeCompare(b.eventSortDate)
      || a.eventTime.localeCompare(b.eventTime)
      || a.name.localeCompare(b.name)
    ));
  }, [approvedSpeakersQuery.data, curatedQuery.data, catalog]);
  const nearestEvent = getNearestUpcomingEvent(catalog);
  const earliestUpcomingIndex = useMemo(() => getEarliestUpcomingSpeakerIndex(speakers), [speakers]);
  const centeredSpeakerIndex = speakers.length ? wrapIndex(activePosition, speakers.length) : 0;
  /*
   * The wheel needs a crowd to read as one.
   *
   * It repeats the roster three times and turns continuously, so with two or three
   * cards the same faces come round every few tiles and the motion looks like a
   * fault rather than a carousel. Below the threshold the roster is simply laid
   * out — which is also the honest presentation of a short list.
   */
  const CAROUSEL_MINIMUM = 5;
  const showCarousel = speakers.length >= CAROUSEL_MINIMUM;

  /** Every tile on the wheel: the roster repeated, in order. */
  const tiles = useMemo(() => {
    if (!speakers.length) return [];
    return Array.from({ length: WHEEL_COPIES * speakers.length }, (_, position) => ({
      position,
      speaker: speakers[position % speakers.length],
    }));
  }, [speakers]);

  /**
   * Where the track has to sit for a given tile to be centred.
   *
   * Measured from the DOM rather than from the stylesheet: tile width and gap are
   * both `clamp()` values, so the only reliable source is what the browser
   * actually laid out.
   */
  const geometry = () => {
    const rail = railRef.current;
    const track = trackRef.current;
    const first = track?.children[0] as HTMLElement | undefined;
    const second = track?.children[1] as HTMLElement | undefined;
    if (!rail || !track || !first) return null;
    const tileWidth = first.getBoundingClientRect().width;
    const step = second
      ? second.getBoundingClientRect().left - first.getBoundingClientRect().left
      : tileWidth;
    return { rail: rail.getBoundingClientRect().width, tileWidth, step };
  };

  const xForPosition = (position: number) => {
    const g = geometry();
    if (!g) return 0;
    return g.rail / 2 - (position * g.step + g.tileWidth / 2);
  };

  /**
   * Scale and dim each tile by how far it is from the centre of the rail.
   *
   * Read every frame rather than set from a class per slot. That is what makes a
   * tile grow *through* the intermediate sizes as it travels inward instead of
   * jumping between three fixed ones, and because only transform and opacity
   * change, a tile can never be stretched out of shape.
   */
  const applyDepth = () => {
    const g = geometry();
    const track = trackRef.current;
    if (!g || !track) return;
    const centre = g.rail / 2;
    const trackX = (gsap.getProperty(track, "x") as number) ?? 0;

    Array.from(track.children).forEach((node, position) => {
      const tileCentre = trackX + position * g.step + g.tileWidth / 2;
      // Distance from the middle, in tiles.
      const distance = Math.abs(tileCentre - centre) / g.step;
      const near = Math.min(distance, 3);
      gsap.set(node, {
        scale: 1 - near * 0.12,
        opacity: Math.max(0.28, 1 - near * 0.26),
        filter: `brightness(${(1 - near * 0.09).toFixed(3)}) saturate(${(1 - near * 0.12).toFixed(3)})`,
        zIndex: Math.round(100 - distance * 10),
      });
    });
  };

  /** Move the wheel by a whole number of tiles. */
  const rotate = (delta: number) => {
    if (!speakers.length || !delta) return;
    hasUserNavigatedRef.current = true;
    const track = trackRef.current;
    positionRef.current += delta;
    const next = positionRef.current;
    setActivePosition(next);
    if (!track) return;

    const settle = () => {
      /*
       * Slide back into the middle copy once the movement is over.
       *
       * The copies are identical, so re-seating the track by a whole roster is
       * invisible — and it is what keeps the wheel able to turn forever in either
       * direction without running out of tiles.
       */
      const period = speakers.length;
      const middleLow = period;
      const middleHigh = period * 2 - 1;
      if (positionRef.current < middleLow || positionRef.current > middleHigh) {
        const shift = positionRef.current < middleLow ? period : -period;
        positionRef.current += shift;
        setActivePosition(positionRef.current);
        gsap.set(track, { x: xForPosition(positionRef.current) });
        applyDepth();
      }
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(track, { x: xForPosition(next) });
      applyDepth();
      settle();
      return;
    }

    gsap.to(track, {
      x: xForPosition(next),
      duration: 0.72,
      // Out-eased so the wheel is already moving on the first frame; an in-out
      // curve reads as a pause between the click and the movement.
      ease: "power2.out",
      overwrite: "auto",
      onUpdate: applyDepth,
      onComplete: settle,
    });
  };

  useEffect(() => {
    if (!pendingSpeakerKey || !speakers.length) return;

    const targetIndex = speakers.findIndex(
      speaker => `${speaker.id}:${speaker.eventSlug}` === pendingSpeakerKey,
    );
    if (targetIndex < 0 || targetIndex !== centeredSpeakerIndex) return;

    const targetSpeaker = speakers[targetIndex];
    const openAfterCentering = window.setTimeout(() => {
      setSelectedSpeaker(targetSpeaker);
      setPendingSpeakerKey(null);
    }, 340);

    return () => window.clearTimeout(openAfterCentering);
  }, [centeredSpeakerIndex, pendingSpeakerKey, speakers]);

  /**
   * Seat the track once the tiles exist, and again if the rail is resized.
   *
   * Deliberately not keyed on `activePosition`. It was, and that made every
   * rotation snap the track straight to its destination the moment state changed
   * -- before the tween had drawn a frame. The wheel arrived instantly and the
   * per-frame scaling had nothing left to interpolate, which is why a tile jumped
   * to its final size instead of growing into it.
   */
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || !tiles.length) return;

    const seat = () => {
      gsap.killTweensOf(track);
      gsap.set(track, { x: xForPosition(positionRef.current) });
      applyDepth();
    };

    seat();
    window.addEventListener("resize", seat);
    return () => window.removeEventListener("resize", seat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles.length]);

  /** Seat the opening position once, when the wheel first has somewhere to be. */
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || !tiles.length || hasUserNavigatedRef.current) return;
    /*
     * Choose the opening tile before the browser paints. The old useEffect ran
     * after the first layout pass, so the track briefly centered tile zero and
     * the first screenshot could show only cards to its right.
     */
    const seat = speakers.length + earliestUpcomingIndex;
    positionRef.current = seat;
    setActivePosition(seat);
    gsap.set(track, { x: xForPosition(positionRef.current) });
    applyDepth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles.length, earliestUpcomingIndex, speakers.length]);

  return (
    <section className="speaker-carousel-section" aria-labelledby="featured-speakers-heading">
      <div className="speaker-carousel-section__hero-image" aria-hidden="true" />
      <div className="speaker-carousel-section__hero-gradient speaker-carousel-section__hero-gradient--rose" aria-hidden="true" />
      <div className="speaker-carousel-section__hero-gradient speaker-carousel-section__hero-gradient--gold" aria-hidden="true" />
      <div className="speaker-carousel-section__hero-gradient speaker-carousel-section__hero-gradient--teal" aria-hidden="true" />
      <div className="speaker-carousel-section__aurora speaker-carousel-section__aurora--one" />
      <div className="speaker-carousel-section__aurora speaker-carousel-section__aurora--two" />

      <div className="container speaker-carousel-section__inner">
        <div className="speaker-carousel-section__heading">
          <SectionBadge variant="gradient">2026 Speaker Series</SectionBadge>
          <h1 id="featured-speakers-heading">Where bold thinkers become <span>unstoppable communities.</span></h1>
          <p>Visionary leaders sharing practical perspectives that move people, teams, and organizations forward.</p>
        </div>
      </div>

      {!showCarousel && speakers.length > 0 && (
        <ul className="speaker-roster-grid" aria-label="Featured speakers">
          {speakers.map(speaker => (
            <li key={`${speaker.id}:${speaker.eventSlug}`}>
              <button
                type="button"
                className="speaker-roster-grid__card"
                style={{ "--roster-tone": speaker.color } as React.CSSProperties}
                onClick={() => setSelectedSpeaker(speaker)}
              >
                {speaker.photo
                  ? <ResponsiveImage src={speaker.photo} alt="" aria-hidden="true" maxWidth={320} sizes="180px" />
                  : <span className="speaker-roster-grid__monogram" aria-hidden="true">{speaker.monogram ?? personInitials(speaker.name, "SP")}</span>}
                <strong>{speaker.name}</strong>
                {speaker.org && <em>{speaker.org}</em>}
                {speaker.topic && <span>{speaker.topic}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {showCarousel && <div
        className="speaker-carousel-rail"
        ref={railRef}
        aria-label="Featured speaker carousel"
        /* Declared as a <color> in the stylesheet below, so the browser itself
           interpolates it and the centre card's highlight fades between tones
           rather than cutting. */
        style={{ "--carousel-active-color": speakers[centeredSpeakerIndex]?.color ?? CHI_SPEAKER_TONES[0] } as React.CSSProperties}
      >
        <div className="speaker-carousel-fade-window">
          <div className="speaker-carousel-stage" ref={trackRef}>
            {tiles.map(tile => (
              <SpeakerCarouselCard
                key={tile.position}
                speaker={tile.speaker}
                offset={tile.position - activePosition}
                onSelect={() => {
                  setPendingSpeakerKey(`${tile.speaker.id}:${tile.speaker.eventSlug}`);
                  rotate(tile.position - positionRef.current);
                }}
                onOpenDetails={() => setSelectedSpeaker(tile.speaker)}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          className="speaker-carousel-control speaker-carousel-control--previous"
          onClick={() => rotate(-1)}
          aria-label="Show previous featured speaker"
        >
          <ChevronLeft size={24} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="speaker-carousel-control speaker-carousel-control--next"
          onClick={() => rotate(1)}
          aria-label="Show next featured speaker"
        >
          <ChevronRight size={24} aria-hidden="true" />
          </button>
        </div>}

        <SpeakerDetailDrawer
          speaker={selectedSpeaker}
          speakers={speakers}
          eventGraphicBySlug={eventGraphicBySlug}
          open={Boolean(selectedSpeaker)}
          onOpenChange={open => {
            if (!open) setSelectedSpeaker(null);
          }}
          onSpeakerChange={nextSpeaker => {
            const nextIndex = speakers.findIndex(candidate => candidate.id === nextSpeaker.id && candidate.eventSlug === nextSpeaker.eventSlug);
            if (nextIndex >= 0 && speakers.length) {
              /*
               * Turn the shorter way round the ring. Comparing wrapped indices
               * directly would send the wheel the long way whenever the jump
               * crosses the seam between last and first.
               */
              const forward = wrapIndex(nextIndex - centeredSpeakerIndex, speakers.length);
              rotate(forward <= speakers.length - forward ? forward : forward - speakers.length);
            }
            setSelectedSpeaker(nextSpeaker);
          }}
        />

        <div className="container speaker-carousel-section__inner">
        <p className="speaker-carousel-position" aria-live="polite">
          <span>{String(centeredSpeakerIndex + 1).padStart(2, "0")}</span>
          <i aria-hidden="true" />
          {String(speakers.length).padStart(2, "0")}
        </p>

        <div
          className="speaker-carousel-featured-detail"
        >
          <ResponsiveImage
            className="speaker-carousel-featured-detail__image"
            src="/media/events/next-calendar-community.webp"
            alt=""
            aria-hidden="true"
            maxWidth={1280}
            sizes="min(71rem, 100vw)"
          />
          {/* Only when the catalog has an event to name — it can be empty now. */}
          {nearestEvent && (
            <div>
              <p className="speaker-carousel-featured-detail__eyebrow">Next on the calendar · {getShortDate(nearestEvent.date)}</p>
              <h2>{nearestEvent.title}</h2>
              <p>{nearestEvent.subtitle || nearestEvent.speakerBio || nearestEvent.description}</p>
            </div>
          )}
          <button
            type="button"
            className="speaker-carousel-featured-detail__cta"
            disabled={!nearestEvent}
            onClick={() => { if (nearestEvent) setLocation(`/events/${nearestEvent.slug}`); }}
          >
            View upcoming event <CtaIcon icon={CalendarDays} />
          </button>
        </div>
      </div>
    </section>
  );
}

/** How long each promo slide holds before the rotator advances. */
const PROMO_SLIDE_INTERVAL_MS = 7000;

export default function FeaturedSpeakers() {
  const { openSpeakerApplication } = useSpeakerApplication();
  const speakerApplicationCardRef = useRef<HTMLDivElement>(null);
  const promoSlidesRef = useRef<Array<HTMLDivElement | null>>([]);
  const previousPromoSlideRef = useRef<number | null>(null);
  const [activePromoSlide, setActivePromoSlide] = useState(0);
  const catalogQuery = trpc.events.publicCatalog.useQuery(undefined, { staleTime: 60_000 });
  /*
   * Undefined when the catalog is empty, which the bundled array never was. The
   * banner below renders only when there is an event to name.
   */
  const nearestEvent = getNearestUpcomingEvent(catalogQuery.data ?? []);
  const promoSlideCount = 2;

  useEffect(() => {
    if (promoSlideCount < 2) {
      setActivePromoSlide(0);
      return;
    }

    const timer = window.setInterval(() => {
      setActivePromoSlide(previous => (previous + 1) % promoSlideCount);
    }, PROMO_SLIDE_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [promoSlideCount]);

  useEffect(() => {
    const card = speakerApplicationCardRef.current;
    if (!card || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const aura = card.querySelector<HTMLElement>(".speaker-apply-section__aura");
    const highlights = Array.from(card.querySelectorAll<HTMLElement>("[data-apply-reveal]"));

    const animateIn = () => {
      gsap.killTweensOf([card, aura, ...highlights]);
      gsap.timeline()
        .to(card, { y: -7, scale: 1.01, duration: 0.34, ease: "power3.out" }, 0)
        .to(aura, { x: 22, scale: 1.16, opacity: 1, duration: 0.46, ease: "power3.out" }, 0)
        .fromTo(highlights, { y: 10, opacity: 0.82 }, { y: 0, opacity: 1, duration: 0.34, stagger: 0.045, ease: "power3.out" }, 0.06);
    };

    const animateOut = () => {
      gsap.killTweensOf([card, aura]);
      gsap.to(card, { y: 0, scale: 1, duration: 0.44, ease: "power3.out" });
      gsap.to(aura, { x: 0, scale: 1, opacity: 0.68, duration: 0.5, ease: "power3.out" });
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (!card.contains(event.relatedTarget as Node | null)) animateOut();
    };

    card.addEventListener("mouseenter", animateIn);
    card.addEventListener("mouseleave", animateOut);
    card.addEventListener("focusin", animateIn);
    card.addEventListener("focusout", handleFocusOut);

    return () => {
      card.removeEventListener("mouseenter", animateIn);
      card.removeEventListener("mouseleave", animateOut);
      card.removeEventListener("focusin", animateIn);
      card.removeEventListener("focusout", handleFocusOut);
      gsap.killTweensOf([card, aura, ...highlights]);
    };
  }, []);

  useLayoutEffect(() => {
    const slides = promoSlidesRef.current.filter((slide): slide is HTMLDivElement => Boolean(slide));
    const entering = promoSlidesRef.current[activePromoSlide];
    const previous = previousPromoSlideRef.current;
    if (!entering || !slides.length) return;

    gsap.killTweensOf(slides);

    if (previous === null || previous === activePromoSlide) {
      gsap.set(slides, { clearProps: "transform,opacity,visibility" });
      previousPromoSlideRef.current = activePromoSlide;
      return;
    }

    const leaving = promoSlidesRef.current[previous];
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(slides, { clearProps: "transform,opacity,visibility" });
      previousPromoSlideRef.current = activePromoSlide;
      return;
    }

    /*
     * Keep both panels mounted so the outgoing card can finish its exit while
     * the selected card enters. The data-active CSS still owns focusability;
     * these inline visibility values only keep the outgoing panel visible for
     * the duration of its GSAP exit.
     */
    if (leaving) {
      gsap.set(leaving, { y: 0, opacity: 1, visibility: "visible" });
    }
    gsap.set(entering, { y: -74, opacity: 0, visibility: "visible" });

    const transition = gsap.timeline({
      defaults: { ease: "power3.out" },
      onComplete: () => {
        if (leaving) gsap.set(leaving, { visibility: "hidden", clearProps: "transform,opacity" });
        gsap.set(entering, { clearProps: "transform,opacity,visibility" });
      },
    });
    if (leaving) {
      transition.to(leaving, { y: 74, opacity: 0, duration: 0.48 }, 0);
    }
    transition.to(entering, { y: 0, opacity: 1, duration: 0.68 }, 0.08);
    previousPromoSlideRef.current = activePromoSlide;

    return () => {
      transition.kill();
    };
  }, [activePromoSlide]);

  return (
    <div className="featured-speakers-page">
      <Navigation />
      <ContinuousSpeakerCarousel />

      <section id="speaker-application" className="speaker-promo-rotator" aria-label="Ways to take part in The Chi Alliance">
        <div className="container">
          <div className="speaker-promo-rotator__tabs" role="tablist" aria-label="Speaker page highlights">
            <button
              id="speaker-promo-tab-events"
              type="button"
              role="tab"
              aria-selected={activePromoSlide === 0}
              aria-controls="speaker-promo-panel-events"
              className="speaker-promo-rotator__tab"
              data-active={activePromoSlide === 0}
              onClick={() => setActivePromoSlide(0)}
            >
              <CalendarDays size={15} aria-hidden="true" />
              <span>Upcoming Events</span>
            </button>
            <button
              id="speaker-promo-tab-apply"
              type="button"
              role="tab"
              aria-selected={activePromoSlide === 1}
              aria-controls="speaker-promo-panel-apply"
              className="speaker-promo-rotator__tab"
              data-active={activePromoSlide === 1}
              onClick={() => setActivePromoSlide(1)}
            >
              <Mic size={15} aria-hidden="true" />
              <span>Apply to Speak</span>
            </button>
          </div>

          <div className="speaker-promo-rotator__stage">
            <div
              ref={node => { promoSlidesRef.current[0] = node; }}
              id="speaker-promo-panel-events"
              className="speaker-promo-rotator__slide"
              data-active={activePromoSlide === 0}
              role="tabpanel"
              aria-labelledby="speaker-promo-tab-events"
              aria-hidden={activePromoSlide !== 0}
              inert={activePromoSlide !== 0 ? true : undefined}
            >
              <div className="speaker-promo-rotator__panel">
                <div className="speaker-promo-rotator__subject">
                  {nearestEvent && (
                    <p className="speaker-promo-rotator__eyebrow">Next on the calendar · {getShortDate(nearestEvent.date)}</p>
                  )}
                  <h2>Make room for <span>what’s next.</span></h2>
                  {/* The heading stands alone when there is no event yet. */}
                  {nearestEvent && (
                    <p className="speaker-promo-rotator__copy">{nearestEvent.title} — {nearestEvent.subtitle || nearestEvent.description}</p>
                  )}
                  <div className="speaker-promo-rotator__actions">
                    <Link href="/calendar-of-events" className="speaker-promo-rotator__button">
                      <CalendarDays size={16} aria-hidden="true" />
                      <span>Explore upcoming events</span>
                      <ArrowRight size={16} aria-hidden="true" />
                    </Link>
                  </div>
                </div>
                <div className="speaker-promo-rotator__divider" aria-hidden="true" />
                <div className="speaker-promo-rotator__visual">
                  <div className="speaker-promo-rotator__lordicon-frame" role="img" aria-label="Animated microphone representing insight and ideas">
                    <LordIcon
                      className="speaker-promo-rotator__lordicon speaker-promo-rotator__lordicon--insight"
                      src="/media/icons/insight-microphone-recording.json"
                      trigger="loop"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              ref={node => { promoSlidesRef.current[1] = node; }}
              id="speaker-promo-panel-apply"
              className="speaker-promo-rotator__slide"
              data-active={activePromoSlide === 1}
              role="tabpanel"
              aria-labelledby="speaker-promo-tab-apply"
              aria-hidden={activePromoSlide !== 1}
              inert={activePromoSlide !== 1 ? true : undefined}
            >
              <div ref={speakerApplicationCardRef} className="speaker-promo-rotator__panel speaker-apply-section__inner">
                <div className="speaker-apply-section__aura" aria-hidden="true" />
                <div className="speaker-promo-rotator__subject speaker-apply-section__content">
                  <p data-apply-reveal>Share your expertise</p>
                  <h2 data-apply-reveal>Bring the insight <span>only you can share.</span></h2>
                  <p className="speaker-apply-section__copy" data-apply-reveal>If your work helps people think differently, grow stronger, or move forward, The Chi Alliance is ready for your voice.</p>
                  <button type="button" className="speaker-apply-section__button" onClick={() => openSpeakerApplication("speaker")} aria-haspopup="dialog">
                    <span>Apply to speak</span>
                    <span className="speaker-apply-section__icon-stack" aria-hidden="true">
                      <LordIcon
                        className="speaker-apply-section__lordicon"
                        src="/media/icons/speaker-microphone.lottie"
                        target=".speaker-apply-section__button"
                        trigger="hover"
                        stroke="light"
                      />
                      <Mic className="speaker-apply-section__fallback-icon" size={17} />
                    </span>
                  </button>
                </div>
                <div className="speaker-promo-rotator__divider" aria-hidden="true" />
                <div className="speaker-promo-rotator__visual speaker-apply-section__visual">
                  <div className="speaker-promo-rotator__lordicon-frame" role="img" aria-label="Animated microphone representing a speaker sharing insight">
                    <LordIcon
                      className="speaker-promo-rotator__lordicon speaker-promo-rotator__lordicon--speaker"
                      src="/media/icons/speaker-microphone.lottie"
                      trigger="loop"
                      stroke="light"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        /* Obsidian Pulse: the active speaker is framed by a living-network field; key tile headlines use the brand’s mixed-case Syne display cadence. */
        .featured-speakers-page { background: #000; color: #F8FAFC; min-height: 100vh; overflow: hidden; }
        .featured-speakers-page button, .featured-speakers-page a { -webkit-tap-highlight-color: transparent; }
         .speaker-carousel-section { position: relative; isolation: isolate; overflow: hidden; padding: clamp(7.5rem, 12vw, 10.5rem) 0 clamp(4.75rem, 7vw, 7rem); background: radial-gradient(ellipse 52% 42% at 82% 23%, rgba(70,198,223,.16), transparent 72%), radial-gradient(ellipse 48% 44% at 18% 30%, rgba(233,30,140,.20), transparent 72%), radial-gradient(circle at 50% 14%, rgba(233,30,140,.24), transparent 34%), radial-gradient(circle at 17% 76%, rgba(61,182,88,.14), transparent 30%), #02030A; }
          .speaker-carousel-section__hero-image { position:absolute; z-index:0; top:0; right:0; left:0; height:clamp(31rem, 58vw, 50rem); pointer-events:none; opacity:.9; background-image:linear-gradient(90deg, #050914 0%, rgba(5,9,20,.72) 24%, rgba(5,9,20,.16) 56%, rgba(5,9,20,.48) 100%), linear-gradient(180deg, rgba(5,9,20,.08) 0%, rgba(5,9,20,.26) 66%, #000 100%), url('/media/backgrounds/featured-speakers-ted-style-hero.jpg'); background-position:center, center, center 30%; background-repeat:no-repeat; background-size: cover; filter:saturate(1.45) contrast(1.1) brightness(1.08); }
         .speaker-carousel-section__hero-gradient { position:absolute; z-index:0; width:min(46rem, 72vw); aspect-ratio:1.26; border-radius:50%; pointer-events:none; filter:blur(42px); mix-blend-mode:screen; opacity:.62; will-change:transform, opacity; animation:speaker-hero-drift 18s cubic-bezier(.45,.05,.55,.95) infinite alternate; }
         .speaker-carousel-section__hero-gradient--rose { top:4rem; left:-14rem; background:radial-gradient(ellipse, rgba(233,30,140,.72) 0%, rgba(233,30,140,.22) 42%, transparent 70%); }
         .speaker-carousel-section__hero-gradient--gold { top:7rem; left:28%; width:min(34rem, 48vw); background:radial-gradient(ellipse, rgba(245,166,35,.48) 0%, rgba(245,166,35,.14) 45%, transparent 72%); animation-delay:-6s; animation-duration:21s; }
         .speaker-carousel-section__hero-gradient--teal { top:1rem; right:-17rem; width:min(42rem, 62vw); background:radial-gradient(ellipse, rgba(70,198,223,.52) 0%, rgba(61,182,88,.15) 44%, transparent 72%); animation-delay:-11s; animation-duration:24s; }
        @keyframes speaker-hero-drift { 0% { transform:translate3d(-2%, -1%, 0) scale(.94); opacity:.28; } 48% { transform:translate3d(8%, 5%, 0) scale(1.07); opacity:.52; } 100% { transform:translate3d(15%, -5%, 0) scale(.98); opacity:.34; } }
        .speaker-carousel-section::before { position:absolute; z-index:-1; top:2.2rem; right:-12rem; width:47rem; height:47rem; content:""; opacity:.28; pointer-events:none; background:repeating-conic-gradient(from 18deg at 50% 50%, rgba(233,30,140,.62) 0 1px, transparent 1.5px 11deg); -webkit-mask:radial-gradient(circle, transparent 0 11%, #000 12% 15%, transparent 16% 27%, #000 28% 29%, transparent 30% 100%); mask:radial-gradient(circle, transparent 0 11%, #000 12% 15%, transparent 16% 27%, #000 28% 29%, transparent 30% 100%); }
        .speaker-carousel-section::after { position:absolute; z-index:-1; left:8%; top:22rem; width:10rem; height:10rem; content:""; opacity:.38; pointer-events:none; background:radial-gradient(circle at 12% 72%, #3DB658 0 2px, transparent 2.8px), radial-gradient(circle at 51% 36%, #E91E8C 0 2px, transparent 2.8px), radial-gradient(circle at 88% 67%, #DBA514 0 2px, transparent 2.8px); }
        .speaker-carousel-section__aurora { position: absolute; pointer-events: none; border-radius: 999px; filter: blur(14px); opacity: .75; z-index: -1; }
        .speaker-carousel-section__aurora--one { width: min(52rem, 60vw); height: min(36rem, 45vw); left: -18rem; top: 14rem; background: radial-gradient(ellipse, rgba(233,30,140,.16), transparent 68%); }
        .speaker-carousel-section__aurora--two { width: min(58rem, 64vw); height: min(34rem, 40vw); right: -22rem; top: 12rem; background: radial-gradient(ellipse, rgba(61,182,88,.11), transparent 70%); }
        /* The roster grid joins these: the section paints an overlay, and anything not
           lifted above it renders washed out. */
        .speaker-carousel-section__inner, .speaker-carousel-rail, .speaker-roster-grid { position: relative; z-index:1; }
        .speaker-carousel-section__heading { max-width: 46rem; margin: 0 auto clamp(2.25rem, 4.6vw, 3.8rem); text-align: center; }
        .speaker-carousel-section__heading > div { display: inline-flex; }
        .speaker-carousel-section__heading h1 { margin: 1rem 0 .9rem; color: #F8FAFC; font-family: 'DM Serif Display', Georgia, serif; font-size: 72px; font-weight: 300; letter-spacing: -.07em; line-height: 1.12; }
        .speaker-carousel-section__heading h1 span, .speaker-apply-section__inner h2 span { background: linear-gradient(102deg, #E91E8C 8%, #DBA514 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .speaker-carousel-section__heading p { max-width: 35rem; margin: 0 auto; color: #9BA8BE; font-family: 'Space Grotesk', sans-serif; font-size: clamp(1rem, 1.45vw, 1.125rem); line-height: 1.7; }
        /* The wheel's window. Tiles outside it are clipped rather than wrapped, so
           the roster can be as long as it likes without the rail growing. */
        /*
          The roster below the carousel threshold.

          A grid rather than a stalled wheel. The wheel repeats the roster three
          times and turns continuously, so with four or fewer cards the same faces
          come round every few tiles and the motion reads as a fault. This is the
          honest presentation of a short list, and it needs no motion to work.
        */
        .speaker-roster-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
          gap: clamp(.85rem, 1.5vw, 1.4rem);
          width: min(100%, 72rem);
          margin: 0 auto;
          padding: 0;
          list-style: none;
        }

        .speaker-roster-grid__card {
          display: grid;
          justify-items: center;
          gap: .45rem;
          width: 100%;
          padding: 1.35rem 1.1rem 1.5rem;
          border: 1px solid color-mix(in srgb, var(--roster-tone) 34%, transparent);
          border-radius: 1.1rem;
          background:
            linear-gradient(180deg, color-mix(in srgb, var(--roster-tone) 12%, transparent), rgba(8, 12, 24, .55));
          color: #F8FAFC;
          text-align: center;
          cursor: pointer;
          transition: transform 180ms ease, border-color 180ms ease;
        }

        .speaker-roster-grid__card:hover { transform: translateY(-3px); border-color: var(--roster-tone); }

        .speaker-roster-grid__card img,
        .speaker-roster-grid__monogram {
          width: 6.5rem;
          height: 6.5rem;
          border-radius: 50%;
          object-fit: cover;
          box-shadow: 0 0 0 2px color-mix(in srgb, var(--roster-tone) 62%, transparent);
        }

        .speaker-roster-grid__monogram {
          display: grid;
          place-items: center;
          background: color-mix(in srgb, var(--roster-tone) 22%, rgba(8, 12, 24, .8));
          color: var(--roster-tone);
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.6rem;
          font-weight: 800;
        }

        .speaker-roster-grid__card strong {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.05rem;
          font-weight: 750;
        }

        .speaker-roster-grid__card em {
          color: #AEB9C9;
          font-family: 'Inter', sans-serif;
          font-size: .84rem;
          font-style: normal;
        }

        .speaker-roster-grid__card span {
          color: #7C899D;
          font-family: 'Inter', sans-serif;
          font-size: .82rem;
          line-height: 1.5;
        }

         .speaker-carousel-rail { position: relative; width: min(100%, calc((var(--tile-w) * 5) + (var(--tile-gap) * 4))); margin: 0 auto; padding: 0; overflow: hidden; --tile-w: clamp(13rem, 19vw, 20rem); --tile-gap: clamp(.75rem, 1.15vw, 1.45rem); }
         /* Alpha masking makes the outer portraits genuinely transparent: the
            section background shows through instead of being painted over with a
            dark or branded gradient. */
         .speaker-carousel-fade-window { --rail-fade:clamp(2.25rem, 6vw, 5.5rem); width:100%; overflow:hidden; -webkit-mask-image:linear-gradient(90deg, transparent 0, #000 var(--rail-fade), #000 calc(100% - var(--rail-fade)), transparent 100%); mask-image:linear-gradient(90deg, transparent 0, #000 var(--rail-fade), #000 calc(100% - var(--rail-fade)), transparent 100%); }
        @property --carousel-active-color { syntax: '<color>'; inherits: true; initial-value: #E91E8C; }
        /* One track carrying every tile, moved as a single object. */
        .speaker-carousel-stage { display: flex; align-items: center; gap: var(--tile-gap); will-change: transform; }
        .speaker-carousel-card { position: relative; flex: 0 0 var(--tile-w); width: var(--tile-w); height: clamp(23rem, 29vw, 31rem); overflow: hidden; border: 1px solid rgba(255,255,255,.13); border-radius: .32rem; background: #07100A; box-shadow: 0 26px 65px rgba(0,0,0,.36); cursor: pointer; transform-origin: center center; transition: box-shadow 220ms ease, border-color 220ms ease; will-change: transform, opacity, filter; }
        .speaker-carousel-card--active { z-index: 2; opacity: 1; filter: none; transform: scale(1); border-color: color-mix(in srgb, var(--carousel-active-color, var(--speaker-color)) 72%, white); box-shadow: 0 0 0 1px color-mix(in srgb, var(--carousel-active-color, var(--speaker-color)) 30%, transparent), 0 18px 65px color-mix(in srgb, var(--carousel-active-color, var(--speaker-color)) 22%, transparent), 0 34px 80px rgba(0,0,0,.54); }
        .speaker-carousel-card:focus-visible { outline: 2px solid #fff; outline-offset: 5px; }
        .speaker-carousel-card__image, .speaker-carousel-card__portrait-placeholder { position: absolute; inset: 0; width: 100%; height: 100%; }
        .speaker-carousel-card__image { object-fit: cover; object-position: center top; filter: saturate(.84) contrast(1.04); transform:translateY(-3.5%) scale(1.08); transition: transform 460ms cubic-bezier(.22,1,.36,1), filter 220ms ease; }
        .speaker-carousel-card--active .speaker-carousel-card__image { transform:translateY(-3.5%) scale(1.1); filter: saturate(1) contrast(1.05); }
        .speaker-carousel-card__portrait-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1rem; overflow: hidden; color: rgba(255,255,255,.9); background: linear-gradient(142deg, color-mix(in srgb, var(--speaker-color) 56%, #090D1C), #0A1020 65%); }
        .speaker-carousel-card__placeholder-orb { position: absolute; width: 72%; aspect-ratio: 1; border-radius: 50%; background: radial-gradient(circle, color-mix(in srgb, var(--speaker-color) 74%, white) 0%, color-mix(in srgb, var(--speaker-color) 35%, transparent) 31%, transparent 69%); filter: blur(4px); opacity: .9; }
        .speaker-carousel-card__portrait-placeholder svg, .speaker-carousel-card__portrait-placeholder span { position: relative; z-index: 1; }
        .speaker-carousel-card__portrait-placeholder span { font-family: 'Syne', 'Space Grotesk', sans-serif; font-size: clamp(2.6rem, 4vw, 4.5rem); font-weight:700; letter-spacing:-.09em; }
        .speaker-carousel-card__image-shade { position:absolute; inset:auto 0 0; height:42%; background:linear-gradient(180deg, transparent, rgba(8,12,24,.88)); pointer-events:none; }
        .speaker-carousel-card__color-wash { position: absolute; inset: 0; background: linear-gradient(135deg, color-mix(in srgb, var(--speaker-color) 28%, transparent) 0%, transparent 44%); mix-blend-mode: screen; opacity: .82; }
        .speaker-carousel-card__tag { position: absolute; top: 1rem; left: 1rem; z-index: 4; margin: 0; max-width: calc(100% - 4.4rem); overflow: hidden; padding: .4rem .7rem; border: 1px solid color-mix(in srgb, var(--speaker-color) 55%, transparent); border-radius: .55rem; color: #FFFFFF; background: color-mix(in srgb, var(--speaker-color) 28%, rgba(5,9,20,.74)); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); font-family: 'Space Grotesk', sans-serif; font-size: .72rem; font-weight: 750; letter-spacing: .06em; text-transform: uppercase; text-overflow: ellipsis; white-space: nowrap; }
        .speaker-carousel-card__linkedin { position: absolute; top: 1rem; right: 1rem; z-index: 4; width: 2.15rem; height: 2.15rem; display: inline-flex; align-items: center; justify-content: center; border: 1px solid rgba(255,255,255,.24); border-radius: .65rem; color: rgba(255,255,255,.53); background: rgba(5,9,20,.38); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); opacity: .63; transition: color 180ms ease, background 180ms ease, opacity 180ms ease, box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease; }
        .speaker-carousel-card__linkedin:hover, .speaker-carousel-card__linkedin:focus-visible { color: #FFFFFF; opacity: 1; border-color: rgba(255,255,255,.92); background: rgba(255,255,255,.12); box-shadow: 0 0 18px rgba(255,255,255,.85), 0 0 36px rgba(255,255,255,.32); transform: translateY(-1px); outline: none; }
        .speaker-carousel-card__content { position: absolute; z-index: 2; left: 0; right: 0; bottom: 4.5rem; padding: .75rem .95rem .3rem; transform-origin:bottom left; transition:transform 220ms cubic-bezier(.23,1,.32,1),opacity 220ms ease; }
        .speaker-carousel-card__identity { margin-bottom: .62rem; }
        .speaker-carousel-card__name { margin: 0 0 .22rem; color: #fff; font-family: 'Syne', 'Space Grotesk', sans-serif; font-size: clamp(1.55rem, 2vw, 2rem); font-weight:650; letter-spacing:-.032em; line-height:1.08; }
        .speaker-carousel-card__role { margin: 0; max-width: 16rem; color: var(--speaker-color); font-family: 'Space Grotesk', sans-serif; font-size: .78rem; font-weight: 700; letter-spacing: .02em; line-height: 1.4; }
        .speaker-carousel-card__topic { display: block; margin: 0; overflow: visible; color: rgba(255,255,255,.96); font-family: 'Syne', 'Space Grotesk', sans-serif; font-size: clamp(.92rem, 1.14vw, 1.12rem); font-weight:600; letter-spacing:-.025em; line-height:1.22; transition: color 220ms ease; }
        .speaker-carousel-card__date { position: absolute; z-index: 3; bottom: 0; left: 0; right: 0; min-height: 4.25rem; display: flex; align-items: center; gap: .65rem; margin-top:.4rem; padding: .68rem 1rem; border-top: 1px solid color-mix(in srgb, var(--speaker-color) 35%, transparent); background: linear-gradient(90deg, color-mix(in srgb, var(--speaker-color) 17%, rgba(6,10,22,.96)), rgba(6,10,22,.96)); color: var(--speaker-color); }
        .speaker-carousel-card__date > span { display: flex; flex: 1; flex-direction: column; gap: .08rem; min-width: 0; }
        .speaker-carousel-card__date strong { color: #FFFFFF; font-family: 'Inter', sans-serif; font-size: .9rem; font-weight: 700; letter-spacing: .015em; line-height: 1.25; }
        .speaker-carousel-card__date small { overflow: hidden; color: rgba(255,255,255,.76); font-family: 'Inter', sans-serif; font-size: .78rem; font-weight: 500; letter-spacing: .005em; line-height: 1.3; text-overflow: ellipsis; white-space: nowrap; }
        .speaker-carousel-card__date-arrow { transition: transform 180ms ease; }
        .speaker-carousel-card:hover .speaker-carousel-card__date-arrow, .speaker-carousel-card:focus-visible .speaker-carousel-card__date-arrow { transform: translateX(3px); }
        .speaker-carousel-control { position: absolute; z-index: 40; top: calc(50% - 1.55rem); width: 3.1rem; height: 3.1rem; display: inline-flex; align-items: center; justify-content: center; border: 1px solid rgba(255,255,255,.22); border-radius: 50%; color: #FFFFFF; background: rgba(9,13,28,.82); box-shadow: 0 10px 30px rgba(0,0,0,.35); cursor: pointer; backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px); transition: transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease; }
        .speaker-carousel-control:hover, .speaker-carousel-control:focus-visible { border-color: rgba(255,255,255,.75); background: rgba(255,255,255,.13); box-shadow: 0 0 22px rgba(255,255,255,.18); transform: scale(1.06); outline: none; }
        .speaker-carousel-control:active { transform: scale(.96); }
        .speaker-carousel-control--previous { left: clamp(.8rem, 3vw, 3rem); }
        .speaker-carousel-control--next { right: clamp(.8rem, 3vw, 3rem); }
        .speaker-detail-drawer { width:min(54rem, 96vw); max-width:none!important; gap:0; padding:0; overflow:hidden; color:#F8FAFC; border:1px solid rgba(116,91,151,.45); border-right:0; border-radius:1.25rem 0 0 1.25rem; background:linear-gradient(162deg, #07111F 0%, #050D1A 54%, #030A15 100%); box-shadow:-30px 0 90px rgba(0,0,0,.56), -1px 0 0 rgba(233,30,140,.18); }
        .speaker-detail-drawer > button { top:1.25rem; right:1.35rem; z-index:20; display:grid; width:2.8rem; height:2.8rem; place-items:center; border:1px solid rgba(246,224,240,.2); border-radius:50%; color:#F8EAF4; background:rgba(255,255,255,.035); opacity:.84; transition:opacity 160ms ease, background-color 160ms ease, border-color 160ms ease, transform 160ms cubic-bezier(.23,1,.32,1); }
        .speaker-detail-drawer > button:hover, .speaker-detail-drawer > button:focus-visible { border-color:rgba(255,255,255,.52); background:rgba(255,255,255,.09); opacity:1; outline:none; transform:scale(1.04); }
        .speaker-detail-drawer > button svg { width:1.55rem; height:1.55rem; stroke-width:1.55; }
        /* Text and portrait side by side. min-width:0 on the text column is what
           actually prevents the collision: without it a flex child refuses to
           shrink below its content and pushes into the portrait instead of
           wrapping. */
        .speaker-detail-drawer__identity { position:relative; z-index:1; display:flex; align-items:center; gap:clamp(1.45rem, 3vw, 2.35rem); }
        .speaker-detail-drawer__identity-text { flex:1 1 auto; min-width:0; }
        .speaker-detail-drawer__header { position:relative; flex:0 0 auto; gap:.35rem; min-height:max-content; padding:6.2rem clamp(2rem, 5vw, 3.6rem) 1.75rem; overflow:visible; border-bottom:1px solid rgba(127,147,183,.2); background:radial-gradient(circle at 14% 94%, rgba(233,30,140,.08), transparent 30%), #07111F; }
        .speaker-detail-drawer__header::before { position:absolute; top:0; right:0; left:0; height:4.1rem; content:""; border-bottom:1px solid rgba(235,74,168,.24); background:linear-gradient(98deg, #331126 0%, #40142F 42%, #2C112B 100%); box-shadow:inset 0 1px 0 rgba(255,255,255,.05), 0 10px 34px rgba(0,0,0,.18); }
        .speaker-detail-drawer__eyebrow { margin:0; color:#F5A623; font-family:'Space Grotesk',sans-serif; font-size:.65rem; font-weight:800; letter-spacing:.12em; text-transform:uppercase; }
        /* Sized to coexist with the portrait rather than dominate the header. The
           old clamp topped out at 4.8rem, which a name like "Economic Damages
           Analytical Group" could not fit beside an image without collision.
           overflow-wrap breaks a single long word rather than letting it spill. */
        .speaker-detail-drawer__title { margin:0; color:#FFFFFF; font-family:'DM Serif Display', Georgia, serif; font-size:clamp(2.5rem, 4.4vw, 4rem); font-weight:400; letter-spacing:-.045em; line-height:1.02; overflow-wrap:anywhere; background:linear-gradient(102deg, #FFFFFF 4%, #F9EAF4 45%, #E8D8F4 100%); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; text-shadow:0 10px 28px rgba(0,0,0,.22); }
        .speaker-detail-drawer__subtitle { margin-top:.62rem; color:#EF87C2; font-family:'DM Sans','Inter',sans-serif; font-size:clamp(1.02rem, 1.75vw, 1.35rem); font-weight:650; line-height:1.35; background:linear-gradient(100deg, #FF67B7 0%, #D58BE7 57%, #A99CF4 100%); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }
        .speaker-detail-drawer__body { min-height:0; flex:1; overflow-y:auto; overscroll-behavior:contain; padding:2.15rem clamp(1.4rem, 4vw, 3rem) 2.6rem; scrollbar-color:rgba(70,198,223,.52) transparent; scrollbar-width:thin; }
        .speaker-detail-drawer__body::-webkit-scrollbar { width:.42rem; }
         .speaker-detail-drawer__body::-webkit-scrollbar-thumb { border-radius:999px; background:linear-gradient(#E91E8C, #F5A623); }
        /* 30% smaller than it was: the portrait led the drawer and pushed the biography below the fold. */
        .speaker-detail-drawer__portrait { position:relative; width:clamp(6.5rem, 14.7vw, 9.1rem); aspect-ratio:1; min-height:0; margin:0; overflow:hidden; flex:0 0 auto; border:3px solid transparent; border-radius:50%; background:linear-gradient(#08121F,#08121F) padding-box, conic-gradient(from 205deg, #7A2DDA 0deg, #D936B0 78deg, #FF4EA1 155deg, #D91B7E 235deg, #8D35D9 315deg, #7A2DDA 360deg) border-box; box-shadow:0 0 0 .24rem rgba(216,48,154,.09), 0 0 24px rgba(233,30,140,.42), 0 0 56px rgba(140,58,215,.2), 0 18px 46px rgba(0,0,0,.46); }
        .speaker-detail-drawer__portrait::after { position:absolute; inset:0; content:""; pointer-events:none; background:linear-gradient(180deg, transparent 40%, rgba(7,11,22,.46) 100%); }
        .speaker-detail-drawer__portrait img { width:100%; height:100%; position:absolute; inset:0; object-fit:cover; object-position:center top; }
        .speaker-detail-drawer__portrait-fallback { position:absolute; inset:0; display:grid; place-content:center; gap:.25rem; color:#FFFFFF; text-align:center; background:radial-gradient(circle at 50% 35%, color-mix(in srgb, var(--speaker-color) 46%, transparent), transparent 52%); }
        .speaker-detail-drawer__portrait-fallback span { font-family:'Syne','Space Grotesk',sans-serif; font-size:.98rem; font-weight:800; letter-spacing:-.06em; }
        .speaker-detail-drawer__timeline { --drawer-node-size:4.45rem; position:relative; display:flex; min-height:0; flex-direction:column; }
         .speaker-detail-drawer__section, .speaker-detail-drawer__event { position:relative; padding:0 0 1.8rem 6.25rem; }
        .speaker-detail-drawer__section--about { min-height:11rem; padding-bottom:3.1rem; }
        .speaker-detail-drawer__event { min-height:14.2rem; padding-bottom:3.4rem; }
        .speaker-detail-drawer__section p:not(.speaker-detail-drawer__section-label) { max-width:38rem; margin:0; color:#C6D0DF; font-family:'Inter',sans-serif; font-size:clamp(.98rem, 1.45vw, 1.1rem); line-height:1.62; }
        .speaker-detail-drawer__biography { max-height:clamp(10rem, 30vh, 18rem); overflow-y:auto; overscroll-behavior:contain; touch-action:pan-y; -webkit-overflow-scrolling:touch; padding-right:.75rem; scrollbar-width:none; }
        .speaker-detail-drawer__biography::-webkit-scrollbar { display:none; width:0; height:0; }
        .speaker-detail-drawer__biography--overflowing { mask-image:linear-gradient(to bottom, #000 0%, #000 78%, rgba(0,0,0,.72) 87%, transparent 100%); -webkit-mask-image:linear-gradient(to bottom, #000 0%, #000 78%, rgba(0,0,0,.72) 87%, transparent 100%); }
        .speaker-detail-drawer__biography:focus-visible { outline:1px solid rgba(231,164,202,.5); outline-offset:.3rem; border-radius:.3rem; }
        /* Section titles, not eyebrows: at .82rem they vanished against the biography they introduce. */
        .speaker-detail-drawer__section-label { display:flex; min-height:var(--drawer-node-size); align-items:center; margin:0 0 .8rem; color:#E7A4CA; font-family:'DM Sans','Inter',sans-serif; font-size:1.05rem; font-weight:750; letter-spacing:.055em; line-height:1.25; text-transform:uppercase; }
        .speaker-detail-drawer__timeline-node { --node-color:#E91E8C; position:absolute; left:0; top:0; z-index:4; display:grid; place-items:center; width:var(--drawer-node-size); height:var(--drawer-node-size); border:1px solid color-mix(in srgb, var(--node-color) 78%, white); border-radius:50%; color:color-mix(in srgb, var(--node-color) 64%, white); background:radial-gradient(circle at 50% 50%, rgba(255,255,255,.96) 0 2%, color-mix(in srgb, var(--node-color) 62%, white) 5%, color-mix(in srgb, var(--node-color) 28%, #11182A) 24%, color-mix(in srgb, var(--node-color) 12%, #0B1424) 65%, #07101F 100%); box-shadow:0 0 0 .48rem #07101F, inset 0 0 15px color-mix(in srgb, var(--node-color) 46%, transparent), 0 0 14px color-mix(in srgb, var(--node-color) 82%, transparent), 0 0 34px color-mix(in srgb, var(--node-color) 52%, transparent), 0 0 62px color-mix(in srgb, var(--node-color) 22%, transparent); }
        .speaker-detail-drawer__timeline-node::before { position:absolute; inset:.34rem; content:""; pointer-events:none; border:1px solid color-mix(in srgb, var(--node-color) 40%, transparent); border-radius:50%; box-shadow:inset 0 0 17px color-mix(in srgb, var(--node-color) 32%, transparent); }
        .speaker-detail-drawer__timeline-node svg { position:relative; z-index:2; filter:drop-shadow(0 0 5px color-mix(in srgb, var(--node-color) 92%, transparent)) drop-shadow(0 0 11px color-mix(in srgb, var(--node-color) 48%, transparent)); }
        .speaker-detail-drawer__timeline-beam { --beam-start:#E91E8C; --beam-end:#F5A623; position:absolute; z-index:2; top:2.2rem; left:2.12rem; bottom:-2.22rem; width:.48rem; content:""; pointer-events:none; transform:translateX(-50%); clip-path:polygon(29% 0,71% 0,58% 44%,65% 74%,100% 100%,0 100%,35% 74%,42% 44%); background:linear-gradient(180deg, #FFFFFF 0%, color-mix(in srgb, var(--beam-start) 82%, white) 2%, var(--beam-start) 18%, color-mix(in srgb, var(--beam-start) 55%, var(--beam-end)) 52%, var(--beam-end) 84%, color-mix(in srgb, var(--beam-end) 74%, white) 98%, #FFFFFF 100%); filter:drop-shadow(0 0 4px var(--beam-start)) drop-shadow(0 0 9px color-mix(in srgb, var(--beam-end) 78%, transparent)); }
        .speaker-detail-drawer__timeline-beam::before, .speaker-detail-drawer__timeline-beam::after { position:absolute; left:50%; content:""; border-radius:50%; transform:translateX(-50%); }
        .speaker-detail-drawer__timeline-beam::before { top:0; width:.7rem; height:.7rem; background:#FFFFFF; box-shadow:0 0 6px #FFFFFF, 0 0 17px var(--beam-start); }
        .speaker-detail-drawer__timeline-beam::after { bottom:0; width:.88rem; height:.88rem; background:color-mix(in srgb, var(--beam-end) 58%, white); box-shadow:0 0 7px #FFFFFF, 0 0 20px var(--beam-end), 0 0 34px color-mix(in srgb, var(--beam-end) 58%, transparent); }
        .speaker-detail-drawer__timeline-node--about { --node-color:#E91E8C; --next-node-color:#F5A623; }
         .speaker-detail-drawer__timeline-node--event { --node-color:#F5A623; --next-node-color:#F5A623; }
        .speaker-detail-drawer__event { padding-top:0; }
        /* Targeted by class. This used to be a direct-child element selector on
           the section, which at specificity 0,1,1 also matched the summary
           paragraph beside it and beat that paragraph's own 0,1,0 class rule no
           matter the source order — so the session description rendered in this
           label's green, uppercase, 800 weight, and its .1em tracking pushed long
           lines past the panel edge. A class keeps the label styled without
           reaching its siblings. */
        .speaker-detail-drawer__event-card { width:100%; display:grid; grid-template-columns:10.5rem minmax(0,1fr) auto; align-items:center; gap:1.25rem; overflow:hidden; padding:.68rem .8rem .68rem .68rem; border:1px solid rgba(150,170,207,.34); border-radius:.82rem; color:#fff; background:linear-gradient(150deg, rgba(16,27,47,.98), rgba(6,13,26,.98)); box-shadow:inset 0 1px 0 rgba(255,255,255,.04), 0 12px 28px rgba(0,0,0,.18); text-align:left; cursor:pointer; transition:transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease; }
        .speaker-detail-drawer__event-card:hover, .speaker-detail-drawer__event-card:focus-visible { border-color:rgba(245,166,35,.7); box-shadow:0 12px 28px rgba(0,0,0,.28); outline:none; transform:translateY(-2px); }
        .speaker-detail-drawer__event-art { display:block; height:7.15rem; overflow:hidden; border:1px solid rgba(245,166,35,.2); border-radius:.58rem; background:linear-gradient(135deg, rgba(245,166,35,.35), rgba(12,19,34,.95)); }
        .speaker-detail-drawer__event-art img { width:100%; height:100%; object-fit:cover; display:block; }
        .speaker-detail-drawer__event-art svg { width:100%; height:100%; padding:1.6rem; color:#FFE6AB; }
        .speaker-detail-drawer__event-card-copy { display:grid; gap:.38rem; min-width:0; }
        .speaker-detail-drawer__event-card-copy strong { color:#f9fafb; font-family:'DM Serif Display', Georgia, serif; font-size:clamp(1.35rem, 2.2vw, 1.8rem); font-weight:400; line-height:1.12; }
        .speaker-detail-drawer__event-meta { display:flex; flex-wrap:wrap; gap:.38rem .85rem; margin-top:.42rem; }
        .speaker-detail-drawer__event-card-copy small { display:inline-flex; align-items:center; gap:.38rem; color:#AEB9C9; font-family:'Inter',sans-serif; font-size:.78rem; line-height:1.35; }
        .speaker-detail-drawer__event-card-copy small svg { flex:0 0 auto; color:#929EB2; }
        .speaker-detail-drawer__event-location { margin-top:.04rem; }
        /* Body copy, not a heading: the summary explains the session, so it reads
           in the same face and rhythm as the biography above rather than competing
           with the title. Slightly brighter than the bio's #C4CEDD because this is
           the paragraph that decides whether someone attends, but short of the
           title's pure #FFFFFF so the heading still leads. overflow-wrap so a long
           unbroken string cannot run past the panel edge. */
        .speaker-detail-drawer__follow-row { display:flex; flex-wrap:wrap; gap:.72rem; margin-top:1.05rem; }
        /* 2.6rem keeps the tap target above the 40px minimum at the default root
           size, and min-width/min-height hold that floor if the root shrinks. */
        .speaker-detail-drawer__follow-link { display:inline-flex; align-items:center; justify-content:center; width:3rem; height:3rem; min-width:42px; min-height:42px; color:rgba(236,244,255,.76); border:1px solid rgba(180,203,236,.27); border-radius:50%; background:linear-gradient(145deg, rgba(52,67,96,.48), rgba(12,22,39,.78)); box-shadow:inset 0 1px 0 rgba(255,255,255,.12), 0 7px 18px rgba(0,0,0,.2); text-decoration:none; transition: color 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease, transform 180ms ease; }
        .speaker-detail-drawer__follow-link:hover, .speaker-detail-drawer__follow-link:focus-visible { color:#FFFFFF; border-color:color-mix(in srgb, var(--speaker-color) 68%, rgba(255,255,255,.28)); background:linear-gradient(145deg, color-mix(in srgb, var(--speaker-color) 34%, rgba(83,101,137,.52)), rgba(23,31,50,.78)); box-shadow:0 0 0 .18rem color-mix(in srgb, var(--speaker-color) 24%, transparent), 0 10px 24px rgba(0,0,0,.34); outline:none; transform:translateY(-1px); }
        .speaker-detail-drawer__footer { display:flex; flex-direction:row; align-items:center; justify-content:stretch; gap:.75rem; padding:1rem clamp(1.35rem, 3vw, 2.4rem); border-top:1px solid rgba(255,255,255,.11); background:rgba(7,11,22,.97); }
        .speaker-detail-drawer__expand { width:100%; min-height:2.8rem; display:inline-flex; align-items:center; justify-content:center; gap:.56rem; border:1px solid rgba(233,30,140,.45); border-radius:.42rem; color:#FFFFFF; background:linear-gradient(110deg, rgba(233,30,140,.9), rgba(194,24,91,.9)); box-shadow:0 12px 28px rgba(233,30,140,.18); font-family:'Space Grotesk',sans-serif; font-size:.76rem; font-weight:800; letter-spacing:.025em; cursor:pointer; transition:transform 160ms cubic-bezier(.23,1,.32,1), box-shadow 160ms ease, background 160ms ease; }
        .speaker-detail-drawer__expand:hover, .speaker-detail-drawer__expand:focus-visible { background:linear-gradient(110deg, #FF3AA4, #E91E8C); box-shadow:0 16px 32px rgba(233,30,140,.32); outline:2px solid rgba(255,255,255,.76); outline-offset:3px; transform:translateY(-1px); }
        .speaker-detail-drawer__expand:active { transform:scale(.98); }
        .speaker-detail-drawer__pager { display:grid; grid-template-columns:1fr auto 1fr; align-items:center; gap:1rem; min-height:4.25rem; padding:.7rem clamp(1.35rem, 4vw, 3rem); border-top:1px solid rgba(127,147,183,.2); background:linear-gradient(90deg, rgba(45,14,35,.92), rgba(7,17,31,.98) 40%, rgba(5,16,29,.98) 60%, rgba(8,37,44,.88)); box-shadow:0 -14px 34px rgba(0,0,0,.2); }
        .speaker-detail-drawer__pager button { display:inline-flex; align-items:center; gap:.42rem; width:max-content; min-height:2.65rem; padding:.48rem .75rem; border:1px solid transparent; border-radius:.55rem; color:#ECF2FA; background:transparent; font-family:'DM Sans','Inter',sans-serif; font-size:.84rem; font-weight:700; cursor:pointer; transition:background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms cubic-bezier(.23,1,.32,1); }
        .speaker-detail-drawer__pager button:last-child { justify-self:end; }
        .speaker-detail-drawer__pager button:hover, .speaker-detail-drawer__pager button:focus-visible { border-color:rgba(255,255,255,.22); color:#FFFFFF; background:rgba(255,255,255,.07); outline:none; transform:translateY(-1px); }
        .speaker-detail-drawer__pager p { margin:0; color:#8593A8; font-family:'Inter',sans-serif; font-size:.72rem; letter-spacing:.06em; }
        .speaker-detail-drawer__pager p span { color:#FFFFFF; font-weight:750; }
        .speaker-carousel-position { display: flex; align-items: center; justify-content: center; gap: .55rem; margin: clamp(1.75rem, 3vw, 2.4rem) 0 0; color: rgba(255,255,255,.46); font-family: 'Space Grotesk', sans-serif; font-size: .64rem; font-weight: 700; letter-spacing: .12em; }
        .speaker-carousel-position > span { color: #FFFFFF; }
        .speaker-carousel-position i { width: 2.2rem; height: 1px; background: linear-gradient(90deg, #E91E8C, #DBA514); }
        .speaker-carousel-featured-detail { position: relative; isolation: isolate; display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: end; gap: 2rem; max-width: 71rem; min-height: 16.75rem; margin: clamp(2.8rem, 5vw, 4.5rem) auto 0; padding: clamp(1.65rem, 3.8vw, 3rem); overflow: hidden; border: 1px solid rgba(255,255,255,.16); background-color: #070B16; background-position: center right; background-repeat: no-repeat; background-size: cover; box-shadow: 0 26px 58px rgba(0,0,0,.26), inset 0 1px 0 rgba(255,255,255,.06); }
        .speaker-carousel-featured-detail__image { position:absolute !important; z-index:0 !important; inset:0; width:100%; height:100%; object-fit:cover; object-position:center right; }
        .speaker-carousel-featured-detail::before { position:absolute; z-index:1; inset:0; content:""; pointer-events:none; background:linear-gradient(90deg, rgba(7,11,22,0.99) 0%, rgba(7,11,22,0.94) 43%, rgba(7,11,22,0.56) 72%, rgba(7,11,22,0.22) 100%), linear-gradient(180deg, rgba(7,11,22,0.08), rgba(7,11,22,0.68)); }
        .speaker-carousel-featured-detail::after { position: absolute; z-index: -1; inset: 0; content: ""; background: linear-gradient(135deg, rgba(61,182,88,.10), transparent 42%, rgba(233,30,140,.15)); mix-blend-mode: screen; pointer-events: none; }
        .speaker-carousel-featured-detail > * { position: relative; z-index: 1; }
        .speaker-carousel-featured-detail__eyebrow { margin: 0 0 .7rem; color: #3DB658; font-family: 'Space Grotesk', sans-serif; font-size: .66rem; font-weight: 700; letter-spacing: .06em; }
        .speaker-carousel-featured-detail h2 { max-width: 45rem; margin: 0 0 .85rem; color: #FFFFFF; font-family: 'Syne', 'Space Grotesk', sans-serif; font-size: clamp(1.7rem, 3vw, 3rem); font-weight:700; letter-spacing:-.07em; line-height:.98; }
        .speaker-carousel-featured-detail p:not(.speaker-carousel-featured-detail__eyebrow) { max-width: 46rem; margin: 0; color: #9BA8BE; font-family: 'Space Grotesk', sans-serif; font-size: .92rem; line-height: 1.7; }
        .speaker-carousel-featured-detail__cta, .speaker-apply-section__button { display: inline-flex; align-items: center; justify-content: center; gap: .62rem; border: 1px solid rgba(255,255,255,.14); border-radius: .32rem; padding: .84rem 1.15rem; color: #FFFFFF; background: #E91E8C; box-shadow: 0 12px 28px rgba(233,30,140,.22); font-family: 'Space Grotesk', sans-serif; font-size: .76rem; font-weight: 700; letter-spacing: .04em; cursor: pointer; transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease; white-space: nowrap; }
        .speaker-carousel-featured-detail__cta:hover, .speaker-carousel-featured-detail__cta:focus-visible { background: #FF3AA4; box-shadow: 0 14px 34px rgba(233,30,140,.38); outline: none; transform: translateY(-2px); }
        .speaker-promo-rotator { padding: clamp(4.5rem, 8vw, 7rem) 0; background: linear-gradient(120deg, #000 0%, #08150E 55%, #000 100%); }
        .speaker-promo-rotator__tabs { display:flex; justify-content:center; gap:.55rem; margin:0 auto 1.25rem; }
        .speaker-promo-rotator__tab { display:inline-flex; align-items:center; justify-content:center; gap:.5rem; min-height:2.7rem; padding:.55rem .95rem; border:1px solid rgba(255,255,255,.14); border-radius:999px; color:#A8B4C8; background:rgba(255,255,255,.035); font-family:'Space Grotesk', sans-serif; font-size:.72rem; font-weight:700; letter-spacing:.04em; cursor:pointer; transition:color 180ms ease, background 180ms ease, border-color 180ms ease, transform 180ms ease; }
        .speaker-promo-rotator__tab:hover, .speaker-promo-rotator__tab:focus-visible { border-color:rgba(255,255,255,.4); color:#FFFFFF; background:rgba(255,255,255,.08); outline:none; transform:translateY(-1px); }
        .speaker-promo-rotator__tab[data-active="true"] { border-color:rgba(233,30,140,.68); color:#FFFFFF; background:linear-gradient(105deg, rgba(233,30,140,.24), rgba(245,166,35,.16)); box-shadow:0 0 24px rgba(233,30,140,.12); }
        .speaker-promo-rotator__stage { display:grid; }
         .speaker-promo-rotator__slide { grid-area: 1 / 1; opacity: 0; transform: translateY(-4.625rem); visibility: hidden; pointer-events: none; }
         .speaker-promo-rotator__slide[data-active="true"] { opacity: 1; transform: translateY(0); visibility: visible; pointer-events: auto; }
        .speaker-promo-rotator__panel { position:relative; isolation:isolate; display:grid; grid-template-columns:minmax(0, 1fr) 1px minmax(15rem, .78fr); align-items:center; gap:clamp(1.5rem, 4vw, 3.75rem); min-height:20rem; overflow:hidden; padding:clamp(2rem, 4vw, 3.5rem); border:1px solid rgba(233,30,140,.32); background:radial-gradient(circle at 86% 45%, rgba(0,169,194,.1), transparent 27%), linear-gradient(126deg, rgba(8,11,22,.98), rgba(12,14,29,.84) 56%, rgba(9,28,35,.8)); box-shadow:inset 0 1px 0 rgba(255,255,255,.05), 0 24px 70px rgba(0,0,0,.18); will-change:transform; }
        .speaker-promo-rotator__subject { position:relative; z-index:1; max-width:32rem; text-align:left; }
        .speaker-promo-rotator__eyebrow, .speaker-apply-section__content > p:first-child { margin:0; color:#F5A623; font-family:'Space Grotesk', sans-serif; font-size:.66rem; font-weight:700; letter-spacing:.06em; text-transform:uppercase; }
        .speaker-promo-rotator__subject h2, .speaker-apply-section__inner h2 { max-width:40rem; margin:.8rem 0 0; color:#FFFFFF; font-family:'Syne', 'Space Grotesk', sans-serif; font-size:clamp(2rem, 4.2vw, 3.5rem); font-weight:700; letter-spacing:-.075em; line-height:.98; }
        .speaker-promo-rotator__subject h2 span, .speaker-apply-section__inner h2 span { background:linear-gradient(102deg, #E91E8C 8%, #DBA514 100%); -webkit-background-clip:text; background-clip:text; color:transparent; }
        .speaker-promo-rotator__copy, .speaker-apply-section__copy { max-width:27rem; margin:.95rem 0 0; color:#A8B4C8; font-family:'Space Grotesk', sans-serif; font-size:.92rem; line-height:1.7; }
        .speaker-promo-rotator__actions { display:flex; flex-wrap:wrap; gap:.7rem; margin-top:1.9rem; }
        .speaker-promo-rotator__button, .speaker-apply-section__button { display:inline-flex; align-items:center; justify-content:center; gap:.62rem; min-height:3rem; padding:.84rem 1.15rem; border:0; border-radius:.32rem; color:#FFFFFF; background:linear-gradient(102deg, #E91E8C, #F5A623); box-shadow:0 12px 30px rgba(233,30,140,.24); font-family:'Space Grotesk', sans-serif; font-size:.76rem; font-weight:700; letter-spacing:.04em; text-decoration:none; cursor:pointer; transition:transform 180ms ease, box-shadow 180ms ease, background 180ms ease; white-space:nowrap; }
        .speaker-promo-rotator__button:hover, .speaker-promo-rotator__button:focus-visible, .speaker-apply-section__button:hover, .speaker-apply-section__button:focus-visible { background:linear-gradient(102deg, #FF3AA4, #FFB132); box-shadow:0 18px 38px rgba(233,30,140,.38); outline:2px solid rgba(255,255,255,.72); outline-offset:3px; transform:translateY(-2px); }
        .speaker-promo-rotator__divider { align-self:stretch; width:1px; min-height:12rem; background:linear-gradient(180deg, transparent, rgba(255,255,255,.44) 20%, rgba(233,30,140,.7) 50%, rgba(255,255,255,.22) 80%, transparent); }
         .speaker-promo-rotator__visual { position:relative; z-index:1; display:flex; align-items:center; justify-content:center; min-width:0; }
         .speaker-promo-rotator__lordicon-frame { display:grid; place-items:center; width:100%; min-height:18rem; }
         .speaker-promo-rotator__lordicon { display:block; width:clamp(15rem, 26vw, 24rem); height:clamp(15rem, 26vw, 24rem); filter:drop-shadow(0 0 24px rgba(233,30,140,.26)) drop-shadow(0 18px 34px rgba(0,0,0,.32)); }
         .speaker-promo-rotator__lordicon--insight { filter:drop-shadow(0 0 28px rgba(0,169,194,.38)) drop-shadow(0 0 44px rgba(233,30,140,.2)); }
         .speaker-promo-rotator__lordicon--speaker { filter:drop-shadow(0 0 28px rgba(233,30,140,.38)) drop-shadow(0 0 44px rgba(245,166,35,.2)); }
        .speaker-apply-section__inner { border-color:rgba(233,30,140,.32); }
        .speaker-apply-section__aura { position:absolute; z-index:-1; width:22rem; height:22rem; right:-7rem; top:-9rem; border-radius:999px; opacity:.68; pointer-events:none; background:radial-gradient(circle, rgba(245,166,35,.26) 0%, rgba(233,30,140,.14) 38%, transparent 72%); filter:blur(3px); }
        .speaker-apply-section__content { position:relative; z-index:1; max-width:32rem; }
        .speaker-apply-section__icon-stack { position:relative; display:inline-grid; width:1.25rem; height:1.25rem; place-items:center; }
        .speaker-apply-section__lordicon { position:absolute; inset:-.35rem; width:1.95rem; height:1.95rem; z-index:2; }
        .speaker-apply-section__fallback-icon { position:relative; z-index:1; opacity:.35; transition:opacity 160ms ease; }
        .speaker-apply-section__button:hover .speaker-apply-section__fallback-icon, .speaker-apply-section__button:focus-visible .speaker-apply-section__fallback-icon { opacity:.12; }
        @media (max-width: 900px) {
          .speaker-carousel-rail { --tile-w:clamp(11rem, 46vw, 15rem); --tile-gap:clamp(.32rem, 1vw, .7rem); }
          .speaker-carousel-card { height:clamp(22rem, 54vw, 27.5rem); }
          .speaker-carousel-card:not(.speaker-carousel-card--active) .speaker-carousel-card__tag,
          .speaker-carousel-card:not(.speaker-carousel-card--active) .speaker-carousel-card__linkedin,
          .speaker-carousel-card:not(.speaker-carousel-card--active) .speaker-carousel-card__content,
          .speaker-carousel-card:not(.speaker-carousel-card--active) .speaker-carousel-card__date { opacity:0; pointer-events:none; }
          .speaker-carousel-card[data-topic-layout="flush"] .speaker-carousel-card__topic { margin-inline:calc(0rem - .95rem); padding-inline:0; overflow-wrap:anywhere; }
          .speaker-carousel-control { width: 2.65rem; height: 2.65rem; top: calc(50% - 1.3rem); }
          .speaker-carousel-control--previous { left: 1rem; }
          .speaker-carousel-control--next { right: 1rem; }
          .speaker-carousel-featured-detail { grid-template-columns: 1fr; display: grid; align-items: start; }
          .speaker-carousel-featured-detail { min-height: 18rem; padding: 1.6rem; background-position: 68% center; }
          .speaker-carousel-featured-detail__cta { justify-self: start; }
          .speaker-promo-rotator__tabs { margin-bottom:1rem; }
          .speaker-promo-rotator__panel { grid-template-columns:1fr; gap:1.7rem; min-height:auto; }
          .speaker-promo-rotator__subject { max-width:none; }
          .speaker-promo-rotator__divider { width:100%; min-height:1px; height:1px; background:linear-gradient(90deg, transparent, rgba(255,255,255,.44) 20%, rgba(233,30,140,.7) 50%, rgba(255,255,255,.22) 80%, transparent); }
          .speaker-promo-rotator__visual { order:2; justify-content:center; }
           .speaker-promo-rotator__lordicon-frame { min-height:16rem; }
           .speaker-promo-rotator__lordicon { width:clamp(14rem, 48vw, 21rem); height:clamp(14rem, 48vw, 21rem); }
          .speaker-apply-section__content { max-width:none; }
        }
        @media (max-width: 560px) {
          .speaker-carousel-section { padding-top: 6.8rem; }
           .speaker-carousel-section__hero-image { height:31rem; opacity:.82; background-position:center, center, center 24%; }
          .speaker-carousel-section__hero-gradient { filter:blur(32px); opacity:.5; }
          .speaker-carousel-section__heading h1 { font-size: clamp(2.55rem, 13vw, 3.7rem); }
          /* A narrower tile, not fewer tiles: the wheel clips what does not fit,
             so hiding specific offsets would punch holes in a continuous track. */
          .speaker-carousel-rail { --tile-w:clamp(9.5rem, 62vw, 13rem); --tile-gap:.42rem; }
          .speaker-carousel-card { height: 26.2rem; }
          .speaker-carousel-card__content { bottom: 4.7rem; padding: .75rem .9rem .28rem; opacity:0; transform:none; pointer-events:none; }
          .speaker-carousel-card--details-open .speaker-carousel-card__content, .speaker-carousel-card:hover .speaker-carousel-card__content, .speaker-carousel-card:focus-visible .speaker-carousel-card__content { opacity:1; transform:none; }
          .speaker-carousel-card__name { font-size: 1.55rem; }
          .speaker-carousel-card__topic { font-size: .92rem; }
          .speaker-carousel-card[data-topic-layout="flush"] .speaker-carousel-card__topic { margin-inline:calc(0rem - .9rem); }
          .speaker-carousel-card__date { min-height: 4.1rem; padding: .62rem .9rem; }
          .speaker-carousel-card__date strong { font-size: .84rem; }
          .speaker-carousel-card__date small { font-size: .72rem; }
          .speaker-carousel-control { top: calc(50% - 1.2rem); width: 2.35rem; height: 2.35rem; }
          .speaker-carousel-control--previous { left: .55rem; }
          .speaker-carousel-control--next { right: .55rem; }
          .speaker-carousel-featured-detail { gap: 1.3rem; margin-top: 2.6rem; }
           .speaker-promo-rotator__panel { padding:1.6rem; }
          .speaker-detail-drawer { width:100vw; max-width:none; border-left:0; border-radius:0; }
          .speaker-detail-drawer__header { padding:5.25rem 1.15rem 1.35rem; }
          .speaker-detail-drawer__header::before { height:3.65rem; }
          .speaker-detail-drawer > button { top:.7rem; right:.78rem; }
          .speaker-detail-drawer__identity { align-items:flex-start; gap:1rem; }
          .speaker-detail-drawer__title { font-size:clamp(2rem, 8.8vw, 2.75rem); }
          .speaker-detail-drawer__subtitle { margin-top:.4rem; font-size:.93rem; }
          .speaker-detail-drawer__body { padding:1.45rem 1rem 2rem; }
          .speaker-detail-drawer__portrait { width:4.85rem; }
          .speaker-detail-drawer__follow-row { gap:.48rem; margin-top:.72rem; }
          .speaker-detail-drawer__follow-link { width:2.55rem; height:2.55rem; }
          .speaker-detail-drawer__section, .speaker-detail-drawer__event { padding-left:4.55rem; }
          .speaker-detail-drawer__section--about { min-height:10.5rem; padding-bottom:2.5rem; }
          .speaker-detail-drawer__event { min-height:15.25rem; padding-bottom:2.8rem; }
          .speaker-detail-drawer__timeline { --drawer-node-size:3.3rem; }
          .speaker-detail-drawer__timeline-node svg { width:1.35rem; height:1.35rem; }
          .speaker-detail-drawer__timeline-beam { top:1.65rem; left:1.65rem; bottom:-1.65rem; width:.4rem; }
          .speaker-detail-drawer__event-card { grid-template-columns:5.8rem minmax(0,1fr) auto; gap:.7rem; }
          .speaker-detail-drawer__event-art { height:6.2rem; }
          .speaker-detail-drawer__event-card-copy strong { font-size:1.12rem; }
          .speaker-detail-drawer__event-meta { gap:.25rem .55rem; margin-top:.28rem; }
          .speaker-detail-drawer__event-card-copy small { font-size:.68rem; }
          .speaker-detail-drawer__pager { min-height:3.9rem; padding:.55rem .75rem; }
          .speaker-detail-drawer__pager button { min-height:2.55rem; padding:.4rem .55rem; }
        }
        @media (prefers-reduced-motion: reduce) {
          .speaker-carousel-card, .speaker-carousel-card__image, .speaker-carousel-card__linkedin, .speaker-carousel-card__content, .speaker-carousel-control, .speaker-carousel-card__date-arrow, .speaker-carousel-featured-detail__cta, .speaker-apply-section__button, .speaker-apply-section__fallback-icon, .speaker-detail-drawer__follow-link { transition: none !important; }
          .speaker-carousel-section__hero-gradient { animation:none; }
          .speaker-detail-drawer__follow-link:hover, .speaker-detail-drawer__follow-link:focus-visible { transform: none; }
          .speaker-apply-section__fallback-icon { opacity:1; }
          /* Both offers still rotate so neither is lost — the sideways travel is what gets dropped. */
           .speaker-promo-rotator__slide { transform: none !important; transition: none !important; }
           .speaker-promo-rotator__slide[data-active="true"] { transition: none !important; }
        }
      `}</style>
    </div>
  );
}
